import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { buildWhatsAppLink, leadWhatsAppMessage } from "@/lib/whatsapp";

const EMPTY = {
  name: "",
  whatsapp: "",
  product_interest: "",
  quantity: "",
  needed_date: "",
  city_uf: "",
  notes: "",
};

export default function QuoteDialog({ open, onOpenChange, brand = "stamp", presetProduct = "" }) {
  const { settings } = useSite();
  const brandName = brand === "sports" ? "K8 Sports" : "K8 Stamp";
  const [form, setForm] = useState({ ...EMPTY, product_interest: presetProduct });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, product_interest: presetProduct });
      setSaved(false);
    }
  }, [open, presetProduct]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      toast.error("Preencha nome e WhatsApp.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leads", { brand, ...form });
      setSaved(true);
      toast.success("Orçamento registrado! Nossa equipe vai retornar.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const continueWhats = () => {
    if (!settings?.whatsapp_number) {
      toast.error("WhatsApp ainda não cadastrado.");
      return;
    }
    const link = buildWhatsAppLink(
      settings.whatsapp_number,
      leadWhatsAppMessage(brandName, form)
    );
    window.open(link, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-lg bg-[var(--bg-card)] border-[var(--border)] text-[var(--text-main)] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">
            Solicitar orçamento — {brandName}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            Conte o que você precisa. Retornamos com um orçamento sob medida.
          </DialogDescription>
        </DialogHeader>

        {saved ? (
          <div className="py-6 text-center space-y-4" data-testid="quote-success">
            <CheckCircle2 className="mx-auto text-green-400" size={48} />
            <p className="font-semibold">Orçamento registrado com sucesso!</p>
            <p className="text-sm text-[var(--text-muted)]">
              Você pode continuar a conversa agora mesmo pelo WhatsApp.
            </p>
            {settings?.whatsapp_number && (
              <Button
                onClick={continueWhats}
                data-testid="quote-continue-whatsapp"
                className="w-full bg-[#25D366] hover:bg-[#1eb959] text-white"
              >
                <MessageCircle size={18} className="mr-2" /> Continuar no WhatsApp
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">
              Fechar
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input data-testid="quote-name" value={form.name} onChange={set("name")} className="bg-[var(--bg-secondary)]" />
            </div>
            <div>
              <Label>WhatsApp / Telefone *</Label>
              <Input data-testid="quote-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="(81) 90000-0000" className="bg-[var(--bg-secondary)]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Produto de interesse</Label>
                <Input data-testid="quote-product" value={form.product_interest} onChange={set("product_interest")} className="bg-[var(--bg-secondary)]" />
              </div>
              <div>
                <Label>Quantidade aprox.</Label>
                <Input data-testid="quote-quantity" value={form.quantity} onChange={set("quantity")} className="bg-[var(--bg-secondary)]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data necessária</Label>
                <Input data-testid="quote-date" value={form.needed_date} onChange={set("needed_date")} placeholder="ex: 30/08" className="bg-[var(--bg-secondary)]" />
              </div>
              <div>
                <Label>Cidade / UF</Label>
                <Input data-testid="quote-city" value={form.city_uf} onChange={set("city_uf")} className="bg-[var(--bg-secondary)]" />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea data-testid="quote-notes" value={form.notes} onChange={set("notes")} rows={3} className="bg-[var(--bg-secondary)]" />
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Button type="submit" disabled={saving} data-testid="quote-request-submit-button" className="w-full btn-primary font-bold">
                {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Enviar orçamento
              </Button>
              {settings?.whatsapp_number && (
                <Button type="button" variant="outline" onClick={continueWhats} data-testid="quote-whatsapp-fallback" className="w-full border-[var(--border)]">
                  <MessageCircle size={18} className="mr-2" /> Continuar no WhatsApp
                </Button>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
